/*
		       FILENAME: navbar.component.ts
		          ROUTE: client/src/app/layouts/public-layout/components/navbar
	             AUTHOR: ICI/AD
 	               WHAT: THIS IS THE NAVBAR COMPONENT FOR DASHBOARD HEADER. WE SEE THIS COMPONENT AS HEADER AFTER LOGGING IN.
	                HOW: THIS NAVBAR COMPONENT IS USED TO DISPLAY THE HEADER OF DASHBOARD. IT CONTAINS THE STATUS DROPDOWN, FILE ID, FACTUAL DATA ID, APPLICATION ID, AND PROFILE.
	    IMPORTING FILES: generic.service, helper.service, home-page.service, loan-application-number.service, auth.service
	  SUBSCRIBING FILES: public-layout.component
	   LAST COMMIT DATE: October 28, 2025
*/

// IMPORTING ANGULAR CORE COMPONENTS
import {AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, OnInit, ViewChild, ViewEncapsulation, inject} from '@angular/core';

// IMPORTING ANGULAR ROUTER COMPONENTS
import {ActivatedRoute, NavigationEnd, Router, RouterModule} from '@angular/router';
import {AppBarPositionMode} from '@progress/kendo-angular-navigation';
import {Align} from '@progress/kendo-angular-popup';
import * as svgIcons from '@progress/kendo-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {GenericService} from 'src/app/services/generic.service';
import {HelperService} from 'src/app/services/helper.service';
import {HomePageService} from 'src/app/services/home-page/home-page.service';
import {LoanApplicationNumberService} from 'src/app/services/storage/loan-application-number.service';
import {AuthService} from '../../../../services/auth.service';
import {RESET_ALLOWED_STATUS, ResetFileApisComponent} from 'src/app/modules/loanapplication/components/reset-file-apis/reset-file-apis.component';
import {ShareWithCustomerComponent} from 'src/app/modules/loanapplication/components/share-with-customer/share-with-customer.component';

// IMPORTING ANGULAR STANDALONE COMPONENTS
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';

// IMPORTING KENDO UI COMPONENTS
import {ButtonsModule} from '@progress/kendo-angular-buttons';
import {DialogsModule} from '@progress/kendo-angular-dialog';
import {DropDownsModule} from '@progress/kendo-angular-dropdowns';
import {LabelModule} from '@progress/kendo-angular-label';
import {Subject} from 'rxjs';
import {filter, finalize, takeUntil} from 'rxjs/operators';
import {ValidationErrorDialogComponent, ValidationErrorPayload} from 'src/app/shared/components/validation-error-dialog/validation-error-dialog.component';
import {DEFAULT_ERROR_MESSAGE, DEFAULT_OFFLINE_MESSAGE} from 'src/app/services/messages/default-messages';

// IMPORTING CUSTOM COMPONENTS
@Component
({
	encapsulation: ViewEncapsulation.None as const,
	selector: 'app-navbar',
	templateUrl: './navbar.component.html',
	styleUrls: ['./navbar.component.scss'],
	standalone: true,
	imports:
	[
		CommonModule,
		FormsModule,
		RouterModule,
		DropDownsModule,
		LabelModule,
		DialogsModule,
		ButtonsModule,
		ValidationErrorDialogComponent,
		ResetFileApisComponent,
		ShareWithCustomerComponent
	]
})

/**
 * NAVBARCOMPONENT IS A COMPONENT THAT DISPLAYS THE NAVBAR FOR THE PUBLIC LAYOUT.
 * IT IS USED TO DISPLAY THE NAVBAR FOR THE PUBLIC LAYOUT.
 */
export class NavbarComponent implements OnInit, AfterViewInit
{
	// SUBJECT TO CANCEL PREVIOUS API REQUESTS WHEN A NEW ONE IS INITIATED
	private $cancelPreviousRequest = new Subject<void>();

	// FLAG TO CHECK IF REQUEST IS IN PROGRESS
	public isRequestInProgress: boolean = false;

	// STORING LOAN APPLICATION NUMBER
	public loanAppNumber: any = null;

	// STORING LOAN APPLICATION ID
	public loanAppId: any = null;

	// TRUE WHEN THE OPEN FILE WAS CONVERTED FROM THE CUSTOMER'S OWN PRE-APPROVAL. THE CUSTOMER
	// ALREADY SEES THAT FILE IN THE CUSTOMER PORTAL, SO "SHARE WITH CUSTOMER" IS HIDDEN FOR IT.
	// COMES FROM get-loanapplication-status; FALSE UNTIL THAT ANSWERS, SO A FAILED STATUS CALL
	// LEAVES THE BUTTON AS IT WAS (THE SERVER REJECTS THE SHARE REGARDLESS).
	public customerOriginated = false;

	// STORING FACTUAL DATA ID
	public factualDataId: any = null;

	// STORING FACTUAL DATA ID SUBSCRIBING
	public factualDataIdSubscribing: any;

	@ViewChild ('anchor', {static: false})
	public anchor!: ElementRef<HTMLElement>; // ADD THE DEFINITELY ASSIGNMENT ASSERTION HERE

	@Input() isMobile: any; // OR ANY TYPE

	// STORING ALL ICONS
	public allIcons = svgIcons;

	// STORING POSITION MODE
	public positionMode: AppBarPositionMode = 'sticky';

	// IMPORTING KENDOKA AVATAR
	public kendokaAvatar = 'https://www.telerik.com/kendo-angular-ui-develop/components/navigation/appbar/assets/kendoka-angular.png';
	public margin = {horizontal: -46, vertical: 7};
	public popupAlign: Align = {horizontal: 'right', vertical: 'top'};

	// STORING SHOW
	public show = false;

	// STORING SHOW RESET
	public showReset: boolean = false;

	// CONTROLS THE DELETE-CONFIRMATION DIALOG
	public deleteConfirmOpened: boolean = false;

	// GUARDS AGAINST A SECOND DELETE WHILE ONE IS IN FLIGHT
	public isDeleting: boolean = false;

	// STORING ACTIVE LINK
	public activeLink = '';
	private timestamp = new Date().getTime();

	// STORING CAN EDIT LOAN APPLICATION
	public canEditLoanApplication: boolean = false;
	public currentStatus: any = {text: '', value: null};
	public status = {text: '', value: null};

	// STORING STATUS LIST
	public statusList: any = [];

	// STORING PREVIOUS STATUS
	public previousStatus: any;

	// STORING CHECKED
	public checked: Boolean = false;

	// STORING STATUS DISABLED
	public statusDisabled: any = false;

	// THE OPEN FILE'S BANKERS AND LICENSING BLOCK, KEPT SO applyStatusRestrictions() CAN RE-RUN
	// WHEN THE USER'S INFO ARRIVES AFTER THE STATUS.
	// NULL UNTIL A FILE'S STATUS HAS LOADED.
	private statusBankerUsers: any[] | null = null;
	private statusLicenseBlocked = false;

	// STORING CAN DELETE LOAN APPLICATION
	public canDeleteLoanApplication: boolean = false;

	public navLinks =
		[
			{path: '/', text: 'Home'},
		];

	// STORING DIALOG OPENED
	public dialogOpened: boolean = false;

	// STORING DIALOG MESSAGE
	public dialogMessage: any = '';

	// STRUCTURED MISSING-FIELD BREAKDOWN FROM THE SERVER, WHEN IT SENT ONE
	public dialogDetails: ValidationErrorPayload | null = null;

	// ONE LINE ABOVE THE LIST, EXPLAINING WHY THE CHANGE WAS REFUSED
	public dialogIntro: string = '';

	// SUBJECT TO TRIGGERS UNSUBSCRIPTION ON COMPONENT DESTROY
	private $destroy = new Subject<void>();

	@HostListener ('click', ['$event']) click(e: any)
	{
		if (e.srcElement.classList && !['navrow', 'header_cont'].includes (e.srcElement.classList[0]))
		{
			e.stopPropagation();
		}
	}

	@HostListener ('document:click') resetToggle()
	{
		// RESET SHOW RESET
		this.showReset = false;
	}

	// SERVICES USING INJECT() FUNCTION
	private zone = inject (NgZone);
	private router = inject (Router);
	private route = inject (ActivatedRoute);
	private homePageService = inject (HomePageService);
	private genericService = inject (GenericService);
	private loanApplicationNumberService = inject (LoanApplicationNumberService);
	public authService = inject (AuthService);
	private toaster = inject (ToastrService);
	public helperService = inject (HelperService);

	// PROPERTY NEEDED FOR AFTERVIEWINIT
	public isLoanApplicationGenerated: boolean = false;

	// STORING SUBSCRIPTION FOR CLEANUP
	private bankerUpdateSubscription: any;

	// STORING NAVIGATION SUBSCRIPTION
	private navigationSubscription: any;

	// STORING PREVIOUS LOAN APPLICATION ID TO PREVENT UNNECESSARY API CALLS
	private previousLoanAppId: string | null = null;

	// FLAG TO CHECK IF SUBSCRIPTIONS ARE ALREADY INITIALIZED
	private subscriptionsInitialized: boolean = false;

	constructor()
	{
		// RESET LOAN APPLICATION NUMBER
		this.loanAppNumber = null;
	}

	// FUNCTION TO DESTROY THE COMPONENT
	ngOnDestroy()
	{
		// UNSUBSCRIBING FROM THE DESTROY SUBJECT
		this.$destroy.next();

		// COMPLETING THE DESTROY SUBJECT
		this.$destroy.complete();
	}

	// RE-READ THE CURRENT USER'S PERMISSIONS INTO THE TEMPLATE FLAGS.
	private refreshPermissions()
	{
		// CHECK IF USER CAN CHANGE STATUS.
		this.statusDisabled = !this.authService.hasPermission ('EDITSTATUS');
		this.canEditLoanApplication = this.authService.hasPermission ('EDITLOANAPPLICATION');

		// CHECK IF USER CAN DELETE APPLICATION
		this.canDeleteLoanApplication = this.authService.hasPermission ('DELETELOANAPPLICATION');
	}

	// FUNCTION TO INITIALIZE THE COMPONENT
	ngOnInit()
	{
		// ASSIGN A VALUE TO THE ANCHOR PROPERTY IN THE CONSTRUCTOR
		// IF YOU HAVE A SPECIFIC ELEMENTREF INSTANCE TO ASSIGN.
		// OTHERWISE, IT WILL BE ASSIGNED BY VIEWCHILD IN THE LIFECYCLE HOOK.

		// READ PERMISSIONS NOW, THEN AGAIN WHENEVER getUserInfo() LOADS THEM.
		// getUserInfo() IS FIRE-AND-FORGET ON LOGIN, SO ON A FRESH SESSION THE
		// PERMISSIONS ARE STILL UNSET WHEN THIS COMPONENT INITIALIZES.
		this.refreshPermissions();

		this.authService.permissionsLoaded
			.pipe (takeUntil (this.$destroy))
			.subscribe (() =>
			{
				this.refreshPermissions();

				// THE STATUS RESTRICTIONS DEPEND ON THE USER TOO; RE-APPLY THEM NOW IT IS KNOWN.
				this.applyStatusRestrictions();
			});

		// INITIALIZE STATUS SUBSCRIPTIONS (ONLY ONCE)
		this.initializeStatusSubscriptions();

		// SUBSCRIBE TO THE ROUTER EVENTS
		this.router.events
			.pipe (
				filter (event => event instanceof NavigationEnd), // FILTER THE EVENT TO ONLY INCLUDE NAVIGATION END EVENTS
				takeUntil (this.$destroy) // UNSUBSCRIBE FROM THE DESTROY SUBJECT
			)
			.subscribe (() =>
			{
		// GET CURRENT LOAN APPLICATION ID FROM THE ROUTE
		const url = this.router.url;
		const urlArray = url.split ('/');

		// EXTRACT LOAN APPLICATION ID FROM URL PATH SEGMENTS IF PRESENT
		const currentLoanAppId = urlArray[1] === 'loan-application' && urlArray[2] ? urlArray[2] : null;

			// ONLY CANCEL PREVIOUS REQUEST IF LOAN APPLICATION ID HAS CHANGED
			// THIS PREVENTS UNNECESSARY API CANCELLATIONS WHEN NAVIGATING BETWEEN STEPS
			if (this.previousLoanAppId !== currentLoanAppId)
			{
			// CANCEL PREVIOUS REQUEST WHEN SWITCHING TO A DIFFERENT LOAN APPLICATION
			if (this.isRequestInProgress)
			{
				this.$cancelPreviousRequest.next();
				this.$cancelPreviousRequest.complete();
				this.$cancelPreviousRequest = new Subject<void>();

				// RESET REQUEST FLAG AFTER CANCELING PREVIOUS REQUEST
				this.isRequestInProgress = false;
			}

				// UPDATE PREVIOUS LOAN APPLICATION ID
				this.previousLoanAppId = currentLoanAppId;
			}

			// TRIGGER STATUS CHECK (WILL USE EXISTING SUBSCRIPTIONS)
			this.handleStatusChange (true);
			});

		// CHECK IF ON BANKER UPDATE EXISTS BEFORE SUBSCRIBING
		if (this.helperService.onBankerUpdate)
		{
			// SUBSCRIBE TO THE ON BANKER UPDATE EVENT
			this.helperService.onBankerUpdate.pipe (takeUntil (this.$destroy)).subscribe (() =>
			{
				// RESET REQUEST FLAG TO FORCE NEW API CALL AFTER BANKER UPDATE
				this.isRequestInProgress = false;

				// CALL THE GET LOAN APPLICATION STATUS API
				this.getLoanApplicationStatus();
			});
		}
	}

	// FUNCTION TO INITIALIZE THE COMPONENT AFTER VIEW INIT
	ngAfterViewInit()
	{
		if (!this.anchor)
		{
			// RETURN IF ANCHOR IS NOT FOUND
			return;
		}

		// CHECK IF IS LOAN GENERATED PROPERTY EXISTS ON HELPER SERVICE
		if (this.helperService && 'isLoanGenerated' in this.helperService)
		{
			(this.helperService.isLoanGenerated as any).pipe (takeUntil (this.$destroy)).subscribe ((status: boolean) =>
			{
				// SET IS LOAN APPLICATION GENERATED
				this.isLoanApplicationGenerated = status;
			});
		}

		// SET UP RESIZE EVENT HANDLER
		this.zone.runOutsideAngular (() =>
		{
			window.addEventListener ('resize', () =>
			{
				if (this.show)
				{
					this.zone.run (() => this.onToggle());
				}
			});
		});
	}

	// FUNCTION TO TOGGLE LEFT SIDE MENU OPEN
	onLeftSideMenu()
	{
		// TOGGLE LEFT SIDE MENU OPEN
		this.helperService.leftSideMenuOpen = !this.helperService.leftSideMenuOpen;
	}

	// INITIALIZE STATUS SUBSCRIPTIONS (CALLED ONCE IN ngOnInit)
	private initializeStatusSubscriptions()
	{
		// PREVENT MULTIPLE SUBSCRIPTION INITIALIZATIONS
		if (this.subscriptionsInitialized)
		{
			// SUBSCRIPTIONS ALREADY INITIALIZED, EXIT EARLY
			return;
		}

		// MARK SUBSCRIPTIONS AS INITIALIZED TO PREVENT DUPLICATE SETUP
		this.subscriptionsInitialized = true;

		// SUBSCRIBE TO FACTUAL DATA ID STATUS CHANGES (ONLY ONCE)
		this.loanApplicationNumberService.getFactualDataIdStatus().pipe (takeUntil (this.$destroy)).subscribe ((value) =>
		{
			this.factualDataId = this.loanApplicationNumberService.getFactualDataIdKey();
		});

		// SUBSCRIBE TO STATUS CHANGES (ONLY ONCE)
		this.loanApplicationNumberService.getStatus().pipe (takeUntil (this.$destroy)).subscribe ((status) =>
		{
			this.handleStatusChange (status);
		});
	}

	// HANDLE STATUS CHANGE AND LOAD APPLICATION DATA
	private handleStatusChange(status: any)
	{
		// SET ACTIVE LINK
		let url = this.router.url;
		const urlArray = url.split ('/');

		// SET ACTIVE LINK
		this.activeLink = urlArray[1];

		// IF STATUS IS NOT NULL AND ACTIVE LINK IS LOAN APPLICATION THEN GET LOAN APPLICATION STATUS
		if (status && this.activeLink == 'loan-application' && urlArray[2])
		{
			const loanAppDetails = this.loanApplicationNumberService.getKey();

			if (!loanAppDetails) return;

			// SET LOAN APPLICATION NUMBER
			this.loanAppNumber = loanAppDetails?.loanapplicationnumber;

		// CHECK IF LOAN APPLICATION ID HAS CHANGED
		const newLoanAppId = loanAppDetails.loanapplicationid;

		// ONLY CALL API IF LOAN APPLICATION ID HAS CHANGED
		if (this.loanAppId !== newLoanAppId)
		{
			// UPDATE STORED LOAN APPLICATION ID TO NEW VALUE
			this.loanAppId = newLoanAppId;

			// FORGET THE PREVIOUS FILE'S ANSWER UNTIL THIS FILE'S STATUS ARRIVES
			this.customerOriginated = false;

				// RESET REQUEST IN PROGRESS FLAG WHEN SWITCHING TO NEW LOAN APPLICATION
				this.isRequestInProgress = false;

				// CALL THE GET LOAN APPLICATION STATUS API
				this.getLoanApplicationStatus();
			}
		}
		else
		{
			// RESET FACTUAL DATA ID
			this.factualDataId = '';

			// RESET LOAN APPLICATION NUMBER
			this.loanAppNumber = null;

			// RESET LOAN APPLICATION ID
			this.loanAppId = null;

			// RESET CUSTOMER-ORIGINATED FLAG
			this.customerOriginated = false;

			// RESET REQUEST IN PROGRESS FLAG
			this.isRequestInProgress = false;
		}
	}

	// LOAD STATUS ON HEADER (TRIGGERS STATUS CHECK)
	loadStatus()
	{
		// TRIGGER STATUS CHECK BY CALLING HANDLE STATUS CHANGE WITH CURRENT STATUS
		const currentStatus = this.loanApplicationNumberService.getKey();
		if (currentStatus)
		{
			this.handleStatusChange (true);
		}
	}

	// DISABLE ITEM IF DISABLE IS YES
	public isItemDisabled(item: any): boolean
	{
		// RETURN DISABLE OPTION
		return item.dataItem.disable === 'YES';
	}

	// CALL THE GET LOAN APPLICATION STATUS API
	public getLoanApplicationStatus()
	{
		// GET LOAN APPLICATION STATUS
		try
		{
			// CHECK IF LOAN APPLICATION ID IS NOT NULL THEN RETURN
			if (!this.loanAppId)
			{
				console.warn ('getLoanApplicationStatus called without loanAppId; aborting request.');

				// RETURN IF LOAN APPLICATION ID IS NULL
				return;
			}

			// PREVENT MULTIPLE SIMULTANEOUS API CALLS
			if (this.isRequestInProgress)
			{
				console.warn ('Request already in progress, cancelling previous request...');

				// CANCEL THE PREVIOUS REQUEST
				this.$cancelPreviousRequest.next();
				this.$cancelPreviousRequest.complete();

				// CREATE NEW SUBJECT FOR THE NEXT REQUEST
				this.$cancelPreviousRequest = new Subject<void>();
			}

			// CLEAR STATUS.
			this.status = {text: 'Please wait ...', value: null};

			// SET REQUEST IN PROGRESS FLAG TO PREVENT DUPLICATE CALLS
			this.isRequestInProgress = true;

			this.genericService.get (`get-loanapplication-status?loanapplication_id=${this.loanAppId}`)
			.pipe (
				takeUntil (this.$destroy), // UNSUBSCRIBE FROM THE DESTROY SUBJECT
				takeUntil (this.$cancelPreviousRequest) // UNSUBSCRIBE FROM THE CANCEL PREVIOUS REQUEST SUBJECT
			).subscribe
			({
				next: (response) =>
				{
					// RESET REQUEST IN PROGRESS FLAG
					this.isRequestInProgress = false;

					if (response.success)
					{
						// CLEAR STATUS LIST.
						this.statusList = [];

						this.loanApplicationNumberService.setFactualKey (response.data.factualdataloanapplicationid);

						// SET RESPONSE STATUS.
						this.currentStatus = response.data.status;

						// SHARE IT APP-WIDE. THIS METHOD RUNS ON FILE OPEN AND AGAIN AFTER EVERY
						// ACCEPTED STATUS CHANGE, SO IT IS THE ONE PLACE THAT ALWAYS KNOWS THE
						// TRUTH. THE RIGHT SIDE BAR READS IT TO DECIDE WHETHER THE ADMIN
						// API-RESET BUTTONS BELONG ON SCREEN.
						this.helperService.activeLoanApplicationStatus = response.data.status;
						this.pendingDealFellThroughAddress = response.data.propertyaddress || '';

						// CONVERTED FROM THE CUSTOMER'S OWN PRE-APPROVAL: NO SHARE BUTTON
						this.customerOriginated = Boolean(response.data.customeroriginated);

						console.log (this.currentStatus, 'this.currentStatus');

						// SET DROPDOWN STATUS VALUE.
						this.status = {text: this.currentStatus, value: this.currentStatus};

						// SET THE PREVIOUS STATUS IF NOT UPDATE.
						this.previousStatus = this.currentStatus;

						// PUSH INTO STATUS LIST.
						this.statusList.push (this.status);

						console.log (this.status, 'this.status');

						console.log (this.statusList, 'this.statusList');

						// GET CURRENT ROLE STATUS LIST FROM USER INFO.
						let currentRoleStatus = this.authService.userStatuses?.[this.currentStatus];

						if (Array.isArray (currentRoleStatus) && currentRoleStatus.length > 0)
						{
							for (let i = 0; i < currentRoleStatus.length; i++)
							{
								// The current status is already the first entry (pushed above), and
								// role_status may also carry it as an availablestatus — a self-transition
								// row, or simply a status reachable from itself. Skip it rather than
								// render the same option twice.
								if (currentRoleStatus[i].status === this.currentStatus) continue;

								this.statusList.push ({text: currentRoleStatus[i].status, value: currentRoleStatus[i].status, disable: currentRoleStatus[i].disable});
							}
						}

						// DISABLE STATUS BY USING USER INFO, BANKER DATA AND STATE LICENSING.
						this.statusBankerUsers = Array.isArray (response.data.banker_users) ? response.data.banker_users : [];
						this.statusLicenseBlocked = Boolean (response.data.licenseblock?.blocked);
						this.applyStatusRestrictions ();
					}
				},
				error: (err) =>
				{
					console.error (err);

					// RESET REQUEST IN PROGRESS FLAG
					this.isRequestInProgress = false;
				}
			});
		}

		// CATCH ERROR
		catch (error)
		{
			console.error (error);

			// RESET REQUEST IN PROGRESS FLAG
			this.isRequestInProgress = false;
		}
	}

	/**
	 * DECIDE WHETHER THE STATUS DROPDOWN IS DISABLED FOR THE OPEN FILE.
	 *
	 * RUNS WHEN THE FILE'S STATUS LOADS AND AGAIN WHEN getUserInfo() FINISHES. RIGHT AFTER LOGIN THAT
	 * REQUEST IS STILL IN FLIGHT AND localStorage HAS NO userInfo YET: READING .usertype OFF null
	 * THREW INSIDE THE STATUS RESPONSE HANDLER (SENTRY) AND SKIPPED THE STATE-LICENSING FREEZE AFTER
	 * IT. UNTIL THE USER IS KNOWN THE DROPDOWN STAYS DISABLED RATHER THAN GUESSING.
	 */
	private applyStatusRestrictions ()
	{
		// NO FILE STATUS LOADED YET: NOTHING TO DECIDE.
		if (!this.statusBankerUsers)
		{
			return;
		}

		let userInfo: any = this.authService.userInfo;
		if (!userInfo)
		{
			try { userInfo = JSON.parse (localStorage.getItem ('userInfo') || 'null'); } catch { userInfo = null; }
		}

		// SET STATUS DISABLED
		this.statusDisabled = !userInfo;

		// IF USER ROLE IS RevUp Banker // PB, AND A SECONDARY Associated Banker IS ON THE FILE
		if (userInfo?.usertype === 'PB' && this.status?.value !== 'Preapproved' && this.status?.value !== 'Preapproval Ready'
			&& this.statusBankerUsers.some ((x: any) => x?.usertype === 'SB'))
		{
			// SET STATUS DISABLED
			this.statusDisabled = true;
		}

		// STATE LICENSING: a member who is not licensed in this file's state
		// cannot move it forward at all, so the whole dropdown is frozen —
		// not just the Ready statuses the server rejects. Last, so it cannot
		// be undone by the banker-assignment rule above.
		if (this.statusLicenseBlocked)
		{
			this.statusDisabled = true;
		}
	}

	onClickLink(link: string)
	{
		// SET ACTIVE LINK
		this.activeLink = link;

		// NAVIGATE TO A SPECIFIC ROUTE
		this.router.navigate ([link]);
	}

	public onToggle(): void
	{
		// TOGGLE SHOW
		this.show = !this.show;
	}

	public onButtonClick(): void
	{
	}

	public onClickProfile()
	{
		// TOGGLE SHOW RESET
		this.showReset = !this.showReset;
	}

	// STEP 1: OPEN THE CONFIRMATION DIALOG. DELETING A LOAN APPLICATION IS
	// IRREVERSIBLE, SO THE ACTUAL REQUEST IS DEFERRED TO confirmDelete().
	public deleteLoanApplication(loanApplicationId: string)
	{
		// IF USER IS NOT ALLOWED TO DELETE THEN DO NOTHING.
		if (!this.canDeleteLoanApplication || !loanApplicationId)
		{
			// RETURN IF USER IS NOT ALLOWED TO DELETE
			return;
		}

		// CLOSE THE DROPDOWN AND OPEN THE CONFIRMATION DIALOG
		this.showReset = false;
		this.deleteConfirmOpened = true;
	}

	// USER DISMISSED THE CONFIRMATION WITHOUT DELETING.
	public cancelDelete()
	{
		this.deleteConfirmOpened = false;
	}

	// STEP 2: USER CONFIRMED. PERFORM THE IRREVERSIBLE DELETE.
	public confirmDelete()
	{
		// RE-CHECK PERMISSION AND GUARD AGAINST DOUBLE-SUBMIT.
		if (!this.canDeleteLoanApplication || !this.loanAppId || this.isDeleting)
		{
			return;
		}

		this.isDeleting = true;

		// TRY TO DELETE LOAN APPLICATION
		try
		{
			this.genericService.get (`reset-data?loanapplication_id=${this.loanAppId}`).pipe (takeUntil (this.$destroy)).subscribe

			({
				next: (response: any) =>
				{
					if (response.success)
					{
						this.loanApplicationNumberService.removeKey();
						this.loanApplicationNumberService.removeFactualDataIdKey();

						// CLEAR THE UNSAVED-CHANGES FLAG. THE APPLICATION IS DELETED, SO ANY
						// PENDING EDIT STATE IS MOOT, AND AuthGuard BLOCKS NAVIGATION WHILE
						// isFormModified IS TRUE — WHICH WOULD ABORT THE REDIRECT BELOW.
						this.helperService.isFormModified = false;

						this.deleteConfirmOpened = false;
						this.isDeleting = false;
						this.toaster.success ('Loan application deleted successfully.');
						this.router.navigate (['/dashboard']);
					}
					else
					{
						this.isDeleting = false;
						this.toaster.error ('Failed to delete loan application. Please try again.');
						console.error ('something went wrong');
					}
				},
				error: (err) =>
				{
					this.isDeleting = false;
					this.toaster.error ('Failed to delete loan application. Please try again.');
					console.error (err);
				},
			});
		}

		// CATCH ERROR
		catch (error)
		{
			this.isDeleting = false;
			this.toaster.error ('Failed to delete loan application. Please try again.');
			console.error (error);
		}
	}

	public onSwitchChange(value: boolean): void
	{
		if (value)
		{
			this.homePageService.setShowCoBorrower ('yes');
		}
		else
		{
			this.homePageService.setShowCoBorrower ('no');
		}
	}

	/**
	 * MOVING A FILE TO 'Deal Fell Through' BLANKS ITS PROPERTY ADDRESS SERVER-SIDE — THE HOUSE IS
	 * GONE, SO THE ADDRESS GOES WITH IT (step_one.controller.js). THAT IS DESTRUCTIVE AND NOT
	 * OBVIOUS FROM A DROPDOWN, SO IT IS CONFIRMED FIRST.
	 *
	 * HELD HERE WHILE THE CONFIRMATION IS OPEN. NON-NULL MEANS A DIALOG IS SHOWING.
	 */
	public pendingDealFellThrough: {text: string; value: string;} | null = null;

	/**
	 * THE ADDRESS THE FILE HAS NOW, SHOWN IN THAT CONFIRMATION SO IT IS CLEAR WHAT IS LOST.
	 * COMES FROM get-loanapplication-status, WHICH THIS COMPONENT ALREADY READS ON OPEN AND
	 * AFTER EVERY ACCEPTED STATUS CHANGE — NO REQUEST OF ITS OWN.
	 */
	public pendingDealFellThroughAddress = '';

	/**
	 * ONE-SHOT: SET BY confirmDealFellThrough SO THE GUARD IN updateStatus KNOWS THIS CALL IS THE
	 * CONFIRMED ONE AND MUST GO THROUGH. CONSUMED IMMEDIATELY.
	 *
	 * A SEPARATE FLAG RATHER THAN READING pendingDealFellThrough: THAT ONE ALSO DRIVES THE
	 * DIALOG'S *ngIf, SO IT HAS TO BE CLEARED TO CLOSE THE DIALOG — WHICH MADE THE GUARD SEE A
	 * FRESH, UNCONFIRMED CHANGE AND SIMPLY RE-OPEN IT. THE BUTTON LOOKED DEAD.
	 */
	private dealFellThroughConfirmed = false;

	public updateStatus(status: {text: string; value: string;})
	{
		// IF USER IS NOT ALLOWED TO EDIT STATUS THEN DO NOTHING.
		if (this.statusDisabled)
		{
			// RETURN IF USER IS NOT ALLOWED TO EDIT STATUS
			return;
		}

		// CONFIRM BEFORE THE ADDRESS IS THROWN AWAY. THE DROPDOWN HAS ALREADY MOVED TO THE NEW
		// VALUE BY NOW, SO CANCELLING HAS TO PUT IT BACK — revertStatus DOES THAT.
		if (status?.value === RESET_ALLOWED_STATUS && !this.dealFellThroughConfirmed)
		{
			this.pendingDealFellThrough = status;
			return;
		}

		// CONSUMED. A LATER CHANGE BACK TO THIS STATUS MUST ASK AGAIN.
		this.dealFellThroughConfirmed = false;

		const payload =
		{
			loanapplication_id: this.loanAppId,
			status: status.value
		};

		// SET ONLY BY THE SUCCESS BRANCH. ANYTHING ELSE MEANS THE SERVER DID NOT TAKE THE CHANGE.
		let accepted = false;

		this.isRequestInProgress = true;

		// SEND THE DATA TO SERVER TO UPDATE THE STATUS.
		this.genericService.put ('update-loanapplication-status', payload).pipe
		(
			takeUntil (this.$destroy),

			// finalize RUNS ON SUCCESS, ON ERROR, ON UNSUBSCRIBE, AND ON THE EMPTY THAT THE AUTH
			// INTERCEPTOR RETURNS FOR 401/403/422. THAT LAST ONE IS WHY THE DROPDOWN USED TO KEEP
			// SHOWING A STATUS THE SERVER HAD REFUSED — NOTHING RAN TO PUT IT BACK.
			finalize (() =>
			{
				this.isRequestInProgress = false;

				if (!accepted)
				{
					this.revertStatus ();
				}
			})
		)
		.subscribe
		({
			next: (response: any) =>
			{
				if (response?.success)
				{
					accepted = true;

					// THE SERVER BLANKS THE PROPERTY ADDRESS TO TBD AS PART OF THIS MOVE, SO THE
					// STEP 3 FORM ON SCREEN IS NOW STALE. TELL IT TO RE-READ RATHER THAN MAKE THE
					// USER REFRESH — AND BEFORE THEY CAN PRESS SAVE AND PUT THE OLD ADDRESS BACK.
					if (status?.value === RESET_ALLOWED_STATUS)
					{
						this.helperService.onPropertyAddressReset.next();
					}

					// CALL THE GET LOAN APPLICATION STATUS API
					this.getLoanApplicationStatus();
					this.toaster.success ('Status updated successfully.');
					return;
				}

				this.showStatusBlockedDialog (response);
			},
			error: (error: any) =>
			{
				// error.error IS THE PARSED RESPONSE BODY. IT IS null FOR A NETWORK FAILURE OR AN
				// HTML GATEWAY PAGE, WHICH IS WHY THIS MUST BE OPTIONAL-CHAINED — READING
				// error.error.message DIRECTLY THREW INSIDE THE HANDLER AND THE MEMBER SAW NOTHING.
				this.showStatusBlockedDialog (error?.error, error);
			}
		});
	}

	/**
	 * PUT THE DROPDOWN BACK TO THE STATUS THE FILE ACTUALLY HAS.
	 *
	 * previousStatus IS A PLAIN STRING WHILE THE DROPDOWN IS BOUND TO {text, value} OBJECTS VIA
	 * textField/valueField WITH NO valuePrimitive, SO ASSIGNING IT RAW LEFT THE CONTROL BLANK.
	 */
	/** PROCEED WITH THE MOVE TO 'Deal Fell Through'. */
	public confirmDealFellThrough (): void
	{
		const status = this.pendingDealFellThrough;
		this.pendingDealFellThrough = null;

		if (status)
		{
			this.dealFellThroughConfirmed = true;
			this.updateStatus (status);
		}
	}

	/** BACK OUT. THE DROPDOWN ALREADY SHOWS THE NEW VALUE, SO IT HAS TO BE PUT BACK. */
	public cancelDealFellThrough (): void
	{
		this.pendingDealFellThrough = null;
		this.dealFellThroughConfirmed = false;
		this.revertStatus ();
	}

	private revertStatus (): void
	{
		this.status = this.previousStatus
			? {text: this.previousStatus, value: this.previousStatus}
			: {text: '', value: null};
	}

	/**
	 * EXPLAIN A REFUSED STATUS CHANGE.
	 *
	 * PREFERS THE SERVER'S STRUCTURED MISSING-FIELD BREAKDOWN SO THE DIALOG CAN GROUP BY STEP,
	 * AND FALLS BACK TO ITS PLAIN SENTENCE.
	 *
	 * @param body THE PARSED RESPONSE BODY: {success, responseStatus, message, count, error}
	 * @param httpError THE HttpErrorResponse, WHEN THIS CAME THROUGH THE ERROR CHANNEL
	 */
	private showStatusBlockedDialog (body: any, httpError?: any): void
	{
		// res.reply NESTS THE CONTROLLER'S `data` UNDER THE BODY'S `error` KEY FOR 4XX RESPONSES,
		// SO THE BREAKDOWN LIVES ONE LEVEL DEEPER THAN IT LOOKS LIKE IT SHOULD.
		const details = body?.error;

		this.dialogDetails = (details?.missingByStep || details?.missingFields)
			? details as ValidationErrorPayload
			: null;

		if (this.dialogDetails)
		{
			this.dialogIntro = 'This file is missing information that is required before it can move to this status. Please complete the following and try again:';
			this.dialogMessage = '';
		}
		else
		{
			this.dialogIntro = '';
			this.dialogMessage = body?.message
				|| (httpError?.status === 0 ? DEFAULT_OFFLINE_MESSAGE : DEFAULT_ERROR_MESSAGE);
		}

		this.dialogOpened = true;
	}

	public close()
	{
		// RESET DIALOG OPENED
		this.dialogOpened = false;

		// CLEAR THE PAYLOAD SO A STALE LIST CANNOT FLASH ON THE NEXT OPEN
		this.dialogDetails = null;
		this.dialogMessage = '';
		this.dialogIntro = '';
	}
}
